import { IRegionRamResponse } from 'api/region/types';
import { OpenFlag } from 'api/resort/types';
import classNames from 'classnames';
import { OpenStatus } from 'components/blocks/OpenStatus';
import type { ILightboxImage } from 'components/common/Lightbox';
import { Lightbox } from 'components/common/Lightbox';
import { WebcamInfo } from 'components/common/WebcamItem';
import { RegularAd } from 'googlead/components/RegularAd';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { formatAgoIfIn24TimestampOrString } from 'util/formatAgo';
import { formatDateFromStr } from 'util/formatDate';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
import { useVisibilityWindow } from 'util/useVisibilityWindow';
import { fetchRegionCamerasByStatus } from 'api/region/static';
import { Diagnostics } from 'util/diagnostics';
import { REGION_CAM_PAGE } from 'page-components/region/cams';
import { Spinner } from 'components/Spinner';
import { snowDaysAgo } from 'util/snowDaysAgo';
import styles from './styles.module.scss';

export interface IProps {
  isFresh: boolean;
  openFlag: OpenFlag;
  regionId: string;
  cameras: IRegionRamResponse;
  showSnowReport?: boolean;
  showSummitDepth?: boolean;
  blur?: boolean;
}

const AD_EVERY_COUNT = 12;

export const WeatherReportCameras = (
  {
    isFresh,
    regionId,
    cameras,
    openFlag,
    showSnowReport,
    showSummitDepth,
    blur,
  }: IProps,
) => {
  const { t } = useOTSTranslation('common');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pages, setPages] = useState<IRegionRamResponse[]>([cameras]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadMore, setLoadMore] = useState<boolean>(cameras.pagination.count > cameras.pagination.limit);

  const buttonVisible = useVisibilityWindow(buttonRef, false, true, 20);

  const camerasWithSnowInLast7Days = useMemo(() => {
    const allCameras = pages.reduce((acc, page) => (
      page?.data ? [...acc, ...page.data] : acc
    ), [] as typeof cameras.data);

    return allCameras.filter((camera) => {
      const snowDate = camera.webcam?.snow?.date;

      if (!snowDate) {
        return false;
      }

      const daysAgo = snowDaysAgo(snowDate);

      return Number.isFinite(daysAgo) && daysAgo <= 7;
    });
  }, [pages]);

  const loadNextPage = useCallback(() => {
    const nextPage = pages.length;
    setIsLoading(true);
    fetchRegionCamerasByStatus(regionId, openFlag, nextPage, REGION_CAM_PAGE).then((data) => {
      setIsLoading(false);
      setPages([...pages, data]);
      setLoadMore(data.pagination.count > (pages.length + 1) * data.pagination.limit);
    }).catch((e) => {
      Diagnostics.error({
        tag: 'region-cameras',
        error: e,
        message: `Failed to load next page for region ${regionId}`,
        data: { regionId, nextPage, openFlag },
      });
      setIsLoading(false);
    });
  }, [pages, openFlag]);

  useEffect(() => {
    setPages([cameras]);
    setLoadMore(cameras.pagination.count > cameras.pagination.limit);
  }, [cameras]);

  useEffect(() => {
    if (buttonVisible && !isLoading) {
      if (buttonRef.current) {
        const pos = buttonRef.current.getBoundingClientRect();
        const topEdgeIn = pos.y > 0 && pos.y < window.innerHeight;
        const bottomEdgeIn = pos.y + pos.height > 0 && pos.y + pos.height < window.innerHeight;
        const isVisible = topEdgeIn || bottomEdgeIn;

        if (isVisible) {
          setIsLoading(true);
          setTimeout(() => {
            loadNextPage();
          }, 10);
        }
      }
    }
  }, [isLoading, buttonVisible, loadNextPage, setIsLoading]);

  const images: Array<ILightboxImage> = useMemo(() => pages.reduce((arr, page) => {
    const list = page.data || [];
    return [...arr, ...list.map((c) => ({
      title: `${c.resort.title}: ${c.webcam?.title}`,
      alt: `${c.resort.title}: ${c.webcam?.title}`,
      src: c.webcam?.image!,
    })).filter((img) => !!img.src)];
  }, [] as Array<ILightboxImage>),
  [pages]);

  if (!cameras.data) {
    // eslint-disable-next-line no-console
    console.warn('Invalid usage of WeatherReportCameras, should have resorts or cameras');
  }

  const [lightBoxIndex, setLightBoxOpen] = useState<number | null>(null);

  const openImage = useCallback((src: string | undefined) => {
    if (src) {
      const index = images.findIndex((c) => c.src === src);
      setLightBoxOpen(index);
    }
  }, [images]);

  return (
    <>
      <section className={classNames(styles.box, isFresh ? '' : BLUR_CONTENT_STYLE_NAME)}>
        <h3><OpenStatus flag={openFlag} /></h3>
        {cameras && (
          <div>
            {pages.map((page, pageIndex) => {
              const subCameras = page.data;

              return subCameras.map((c, camIndex) => {
                const index = camIndex + pageIndex * REGION_CAM_PAGE;

                const afterEveryCount = (index) % AD_EVERY_COUNT === AD_EVERY_COUNT - 1; // Every AD_EVERY_COUNT
                const lastInBlock = camIndex === cameras.pagination.count - 1;
                const shouldShowAd = afterEveryCount && !lastInBlock;

                return (
                  <React.Fragment key={c.resort.uuid}>
                    <WebcamInfo
                      blur={blur}
                      resort={c.resort}
                      subtitle={
                            c.openFlag === OpenFlag.Closed
                              ? t('region.skiReport.opening', { date: formatDateFromStr(c.openingDate) })
                              : formatAgoIfIn24TimestampOrString(c.webcam?.date || '')
                          }
                      cam={c.webcam}
                      key={c.resort.uuid}
                      showSummitDepth={showSummitDepth}
                      showSnowReport={showSnowReport
                        && camerasWithSnowInLast7Days.some((camera) => camera.resort.uuid === c.resort.uuid)}
                      onClick={() => openImage(c.webcam?.image)}
                    />
                    { shouldShowAd
                          && <RegularAd wrapperClassName={styles.adWrapper} className={styles.ad} />}
                  </React.Fragment>
                );
              });
            })}
          </div>
        )}
        {!isLoading && hasLoadMore && (
        <button
          ref={buttonRef}
          type="button"
          className={styles.loadMore}
          onClick={loadNextPage}
        >
          {t('common.loadMore')}
        </button>
        )}
        {isLoading
          && (
          <div className={styles.boxLoader}>
            <Spinner />
          </div>
          )}
      </section>
      {lightBoxIndex !== null
      && <Lightbox onClose={() => setLightBoxOpen(null)} images={images} index={lightBoxIndex} />}
    </>
  );
};
